import * as swcHelpers from '@swc/helpers';
import * as React from 'react';

const initialState = {
    status: 'uninit',
    cameraError: null
};
function createRandomId(prefix) {
    return `${prefix}${Math.random().toString(16).substring(2)}`;
}
function loadQrcodeModule() {
    return import('html5-qrcode');
}
const useCodeScannerCamera = ({ onCodeDetected , onError , cameraContainerRef , formatsToSupport  })=>{
    const scannerRef = React.useRef(null);
    const cameraContainerId = React.useMemo(()=>createRandomId('scanner-')
    , []);
    const [{ cameraError , status  }, setState] = useStateReducer(swcHelpers.objectSpread({}, initialState));
    const stopScanner = React.useCallback(swcHelpers.asyncToGenerator(function*() {
        var ref;
        const { Html5QrcodeScannerState  } = yield loadQrcodeModule();
        const scanningState = (ref = scannerRef.current) === null || ref === void 0 ? void 0 : ref.getState();
        if (scanningState === Html5QrcodeScannerState.SCANNING) {
            try {
                var ref1;
                yield (ref1 = scannerRef.current) === null || ref1 === void 0 ? void 0 : ref1.stop();
            } catch (e) {
                console.error(e);
            }
        }
        setState({
            status: 'stopped'
        });
    }), [
        setState
    ]);
    const onScanSuccess = React.useCallback((decodedText)=>{
        onCodeDetected(decodedText);
        stopScanner();
    }, [
        onCodeDetected,
        stopScanner
    ]);
    const handleScannerError = React.useCallback((err)=>{
        console.debug('Code Scanner Error:', err);
        setState({
            status: 'error',
            cameraError: err.toLowerCase().includes('permission denied') ? 'permissionDenied' : 'other'
        });
        onError && onError(err);
    }, [
        onError,
        setState
    ]);
    const getAspectRatio = React.useCallback((container)=>{
        const height = container.offsetHeight;
        const width = container.offsetWidth;
        return width / height;
    }, []);
    const getQrBoxSize = React.useCallback(()=>{
        var ref;
        const height = 336;
        let width = 288 //default size
        ;
        var ref2;
        // Check if the width is larger than our Tablet breakpoint, 768
        const containerWidth = (ref2 = (ref = cameraContainerRef.current) === null || ref === void 0 ? void 0 : ref.offsetWidth) !== null && ref2 !== void 0 ? ref2 : 0;
        containerWidth > 768 ? width = 488 : width;
        return {
            height,
            width
        };
    }, [
        cameraContainerRef
    ]);
    const initScanner = React.useCallback(swcHelpers.asyncToGenerator(function*() {
        var ref;
        const { Html5Qrcode  } = yield loadQrcodeModule();
        setState({
            status: 'loading'
        });
        let cameras = [];
        try {
            cameras = yield Html5Qrcode.getCameras();
        } catch (e) {
            handleScannerError(typeof e === 'string' ? e : 'other');
        }
        if (!(cameras === null || cameras === void 0 ? void 0 : cameras.length)) return;
        cameraContainerRef === null || cameraContainerRef === void 0 ? void 0 : (ref = cameraContainerRef.current) === null || ref === void 0 ? void 0 : ref.setAttribute('id', cameraContainerId);
        scannerRef.current = new Html5Qrcode(cameraContainerId, swcHelpers.objectSpread({
            verbose: false
        }, !!(formatsToSupport === null || formatsToSupport === void 0 ? void 0 : formatsToSupport.length) ? {
            formatsToSupport
        } : {}));
        setState({
            status: 'ready'
        });
    }), [
        setState,
        handleScannerError,
        cameraContainerRef,
        cameraContainerId,
        formatsToSupport
    ]);
    const startScanning = React.useCallback(swcHelpers.asyncToGenerator(function*() {
        if (!(scannerRef === null || scannerRef === void 0 ? void 0 : scannerRef.current) || status !== 'ready' || !cameraContainerRef.current) {
            setState({
                status: 'error',
                cameraError: 'other'
            });
            return;
        }
        const aspectRatio = getAspectRatio(cameraContainerRef.current);
        const qrbox = getQrBoxSize();
        try {
            yield scannerRef.current.start({
                facingMode: 'environment'
            }, {
                aspectRatio,
                fps: 10,
                qrbox
            }, onScanSuccess, undefined // Do nothing for unsuccessful scans
            );
        } catch (e) {
            handleScannerError(typeof e === 'string' ? e : 'other');
        }
        setState({
            status: 'scanning'
        });
    }), [
        status,
        cameraContainerRef,
        getAspectRatio,
        getQrBoxSize,
        onScanSuccess,
        handleScannerError,
        setState, 
    ]);
    React.useEffect(()=>{
        if (status === 'ready') {
            startScanning();
        }
    }, [
        startScanning,
        status
    ]);
    return {
        initScanner,
        stopScanner,
        status,
        cameraError
    };
};
function useStateReducer(initialState1) {
    return React.useReducer((state, action)=>swcHelpers.objectSpread({}, state, action)
    , initialState1);
}

export { useCodeScannerCamera };
//# sourceMappingURL=hooks.js.map
